import { useQuery } from '@apollo/client';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { OrdatRow } from './OrdatRow.tsx';
import { GET_ORDAT_INTEGRATIONS } from './queries.ts';
import type { OrdatIntegration } from './types.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Table from '../../../core/components/Table.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ERP_PATH, CUSTOMER_INTEGRATION_ORDAT_ADD_PATH } from '../../constants.ts';

const AddIcon = withIcon(faPlus);

const OrdatIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const { data, loading, refetch } = useQuery(GET_ORDAT_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations: OrdatIntegration[] = data?.ordatIntegrations || [];

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={`${t('backTooltipText')} ${t('erp.pageTitle')}`}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('ordat.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ORDAT_ADD_PATH)}>
                                <AddIcon />
                                {t('ordat.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="w-1/3">{t('ordat.table.name')}</Table.HeadCell>
                                <Table.HeadCell className="w-1/3">{t('ordat.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('ordat.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-40 w-40" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <OrdatRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={4} className="text-center text-content-medium">
                                        {t('ordat.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OrdatIntegrationPage;
